import { useState, useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import LeadFilterPanel, { FilterField, FilterRule, isRuleActive, ruleMatches, STATUS_OPTIONS, PRIORITY_OPTIONS } from "@/components/LeadFilterPanel";
import type { ColumnConfig } from "@/lib/columnConfig";
import { Search, Menu, ArrowUp, ArrowDown, X, Percent } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface Lead {
  id: string;
  lead_name: string;
  company: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  priority: string;
  value: number | null;
  source: string | null;
  conversation_notes: string | null;
  next_action: string | null;
  next_action_date: string | null;
  created_at: string;
  custom_fields?: Record<string, any>;
  last_refreshed_at?: string | null;
  updated_at?: string | null;
}

interface LeadFiltersProps {
  leads: Lead[];
  onFilterChange: (filteredLeads: Lead[]) => void;
  sortField: string | null;
  sortDirection: 'asc' | 'desc';
  onSortChange: (field: string | null, direction: 'asc' | 'desc') => void;
  columnConfig?: ColumnConfig[];
}

const LeadFilters = ({ leads, onFilterChange, sortField, sortDirection, onSortChange, columnConfig = [] }: LeadFiltersProps) => {
  const [filterRules, setFilterRules] = useState<FilterRule[]>([]);

  const filterFields = useMemo<FilterField[]>(() => {
    const base: FilterField[] = [
      { key: "lead_name", label: "Name", kind: "text" },
      { key: "company", label: "Company", kind: "text" },
      { key: "email", label: "Email", kind: "text" },
      { key: "phone", label: "Phone", kind: "text" },
      { key: "status", label: "Status", kind: "enum", options: STATUS_OPTIONS },
      { key: "priority", label: "Priority", kind: "enum", options: PRIORITY_OPTIONS },
      { key: "value", label: "Value", kind: "number" },
      { key: "source", label: "Source", kind: "text" },
      { key: "conversation_notes", label: "Notes", kind: "text" },
      { key: "next_action", label: "Next Action", kind: "text" },
      { key: "next_action_date", label: "Next Action Date", kind: "date" },
      { key: "created_at", label: "Date Added", kind: "date" },
      { key: "updated_at", label: "Date Changed", kind: "date" },
    ];
    const custom = columnConfig
      .filter((c) => c.type !== "default" && !String(c.id).startsWith("default-"))
      .map((c): FilterField => {
        const vals = leads.map((l) => l.custom_fields?.[c.name]).filter((v) => v != null && String(v).trim() !== "").slice(0, 50).map(String);
        const numeric = vals.length > 0 && vals.every((v) => /^-?\$?[\d,]+(\.\d+)?$/.test(v.trim()));
        const dateLike = !numeric && vals.length > 0 && vals.every((v) => /\d/.test(v) && /[-/]|[a-z]{3}/i.test(v) && !isNaN(Date.parse(v)));
        return { key: `cf:${c.name}`, label: c.name, kind: numeric ? "number" : dateLike ? "date" : "text" };
      });
    return [...base, ...custom];
  }, [columnConfig, leads]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const [fuzzyPercent, setFuzzyPercent] = useState(10);

  const clearSort = () => {
    onSortChange(null, 'asc');
    setSortDialogOpen(false);
  };

  // Detect if search term contains a number
  const searchNumber = useMemo(() => {
    const cleaned = searchTerm.replace(/[,$\s]/g, '');
    const num = parseFloat(cleaned);
    return !isNaN(num) && cleaned.length > 0 ? num : null;
  }, [searchTerm]);

  const isNumberSearch = searchNumber !== null;

  useEffect(() => {
    let filtered = [...leads];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();

      filtered = filtered.filter((lead) => {
        // If the search is a pure number, use ONLY fuzzy number matching (no substring text match)
        if (searchNumber !== null) {
          const tolerance = searchNumber * (fuzzyPercent / 100);
          const low = searchNumber - tolerance;
          const high = searchNumber + tolerance;

          // Check value field
          if (lead.value !== null && lead.value >= low && lead.value <= high) {
            return true;
          }

          // Phone numbers are intentionally excluded from fuzzy number matching

          // Check custom fields for numeric values
          if (lead.custom_fields) {
            const cfMatch = Object.values(lead.custom_fields).some((val) => {
              const str = String(val).replace(/[,$\s]/g, '');
              // Only match if the entire value is a number (not "123 Main St")
              if (!/^-?\d+(\.\d+)?$/.test(str)) return false;
              const n = parseFloat(str);
              return n >= low && n <= high;
            });
            if (cfMatch) return true;
          }

          return false;
        }

        // Text-only search (non-numeric input)
        const searchLower = searchTerm.toLowerCase();
        const standardMatch =
          lead.lead_name.toLowerCase().includes(searchLower) ||
          lead.company?.toLowerCase().includes(searchLower) ||
          lead.email?.toLowerCase().includes(searchLower) ||
          lead.phone?.toLowerCase().includes(searchLower) ||
          lead.source?.toLowerCase().includes(searchLower) ||
          lead.conversation_notes?.toLowerCase().includes(searchLower) ||
          lead.next_action?.toLowerCase().includes(searchLower);

        const customFieldsMatch = lead.custom_fields
          ? Object.values(lead.custom_fields).some((value) =>
              String(value).toLowerCase().includes(searchLower)
            )
          : false;

        return standardMatch || customFieldsMatch;
      });
    }

    const kinds = Object.fromEntries(filterFields.map((f) => [f.key, f.kind]));
    const active = filterRules.filter((r) => isRuleActive(r, kinds[r.field]));
    if (active.length) {
      filtered = filtered.filter((lead) =>
        active.every((r) => {
          const raw = r.field.startsWith("cf:") ? lead.custom_fields?.[r.field.slice(3)] : (lead as any)[r.field];
          return ruleMatches(raw, r, kinds[r.field]);
        }),
      );
    }

    onFilterChange(filtered);
    console.log(`[LeadFilters] Filtered ${filtered.length} of ${leads.length} leads. Search term: "${searchTerm}" fuzzy: ${fuzzyPercent}%`);
  }, [searchTerm, leads, fuzzyPercent, filterRules, filterFields]);

  return (
    <div className="mb-3 flex items-center gap-2">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          id="search"
          placeholder="Search by any field..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={`pl-9 ${isNumberSearch && searchTerm ? 'pr-[140px]' : searchTerm ? 'pr-9' : 'pr-3'}`}
        />
            {searchTerm && isNumberSearch && (
              <div className="absolute right-9 top-1/2 -translate-y-1/2 flex items-center gap-1.5 mr-1">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1.5 bg-muted/60 rounded-md px-2 py-1">
                        <Percent className="h-3 w-3 text-muted-foreground shrink-0" />
                        <Slider
                          value={[fuzzyPercent]}
                          onValueChange={(v) => setFuzzyPercent(v[0])}
                          min={0}
                          max={100}
                          step={5}
                          className="w-16"
                        />
                        <span className="text-xs font-medium text-muted-foreground w-7 text-right tabular-nums">
                          {fuzzyPercent}%
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      <p>Fuzzy range: ±{fuzzyPercent}% of {searchNumber}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
      </div>

      <LeadFilterPanel fields={filterFields} rules={filterRules} onChange={setFilterRules} />

      <Dialog open={sortDialogOpen} onOpenChange={setSortDialogOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="shrink-0 gap-2">
            <Menu className="h-4 w-4" />
            <span className="hidden sm:inline">Sort</span>
          </Button>
        </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Sort Leads</DialogTitle>
              <DialogDescription>
                Choose how to sort your leads
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-3">
                <Label className="text-base font-semibold">Sort By</Label>
                <RadioGroup 
                  value={sortField || 'none'} 
                  onValueChange={(value) => {
                    if (value === 'none') {
                      clearSort();
                    } else {
                      onSortChange(value, sortDirection);
                    }
                  }}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="none" id="none" />
                    <Label htmlFor="none" className="font-normal cursor-pointer">No sorting</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="priority" id="priority" />
                    <Label htmlFor="priority" className="font-normal cursor-pointer">Priority</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="created_at" id="created_at" />
                    <Label htmlFor="created_at" className="font-normal cursor-pointer">Date Added</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="updated_at" id="updated_at" />
                    <Label htmlFor="updated_at" className="font-normal cursor-pointer">Date Updated</Label>
                  </div>
                </RadioGroup>
              </div>

              {sortField && sortField !== 'none' && (
                <div className="space-y-3">
                  <Label className="text-base font-semibold">Direction</Label>
                  <RadioGroup value={sortDirection} onValueChange={(value) => onSortChange(sortField, value as 'asc' | 'desc')}>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="asc" id="asc" />
                      <Label htmlFor="asc" className="font-normal cursor-pointer flex items-center">
                        <ArrowUp className="h-4 w-4 mr-1" />
                        Ascending
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="desc" id="desc" />
                      <Label htmlFor="desc" className="font-normal cursor-pointer flex items-center">
                        <ArrowDown className="h-4 w-4 mr-1" />
                        Descending
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
    </div>
  );
};

export default LeadFilters;
